/**
 * Unit 1.4 · IMPL.A: clients table cells.
 *
 * Bead: insurescope-11zkc.2.4.3.1. Design contract:
 * docs/design-briefs/2026-06-03-clients-list.md (token map + row states).
 */
import { formatDistanceToNowStrict } from "date-fns";
import { Clock } from "lucide-react";

import type { IClient } from "@/api";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDate } from "@/utils/formatDate";

import { IndustryChip } from "./IndustryChip";

/** Industry chips show at most this many before collapsing to "+N more". */
const MAX_VISIBLE_CHIPS = 2;

export function ClientNameCell({ client }: { client: IClient }) {
    const visible = client.industries.slice(0, MAX_VISIBLE_CHIPS);
    const overflow = client.industries.slice(MAX_VISIBLE_CHIPS);

    return (
        <div className="flex min-w-0 flex-col gap-1">
            <span className="text-foreground truncate text-sm font-medium">
                {client.name}
            </span>
            {client.industries.length > 0 && (
                <span className="flex flex-wrap items-center gap-1">
                    {visible.map((industry) => (
                        <IndustryChip key={industry.id} name={industry.name} />
                    ))}
                    {overflow.length > 0 && (
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <span className="text-muted-foreground text-xs" />
                                }
                            >
                                +{overflow.length} more
                            </TooltipTrigger>
                            <TooltipContent>
                                {overflow.map((i) => i.name).join(", ")}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </span>
            )}
        </div>
    );
}

export function CountCell({ value }: { value: number }) {
    if (value === 0) {
        return <span className="text-muted-foreground text-sm">—</span>;
    }
    return (
        <span className="text-sm tabular-nums">{value.toLocaleString()}</span>
    );
}

export function LastActivityCell({ value }: { value: string | null }) {
    if (!value) {
        return <span className="text-muted-foreground text-sm">Never</span>;
    }
    return (
        <span
            className="text-muted-foreground flex items-center gap-1.5 text-sm whitespace-nowrap"
            title={formatDate(value)}
        >
            <Clock className="size-3.5 shrink-0" />
            {formatDistanceToNowStrict(new Date(value), { addSuffix: true })}
        </span>
    );
}
